import type {
  AugmentedWarAction,
  AugmentedWarState,
} from "@cloudnest/augmented-war-engine";
import { validateAndReduceGameState } from "@cloudnest/augmented-war-engine";
import { augmentedWarEventToAction } from "./event-adapter";
import type {
  AugmentedWarReplayFixtureEvent,
  AugmentedWarReplayInitPayload,
} from "./replay-fixtures.generated";

/**
 * UI-free Augmented War replay simulation. `init` seeds canonical state; every
 * other event is re-typed by the event adapter and folded through the engine
 * trust boundary `validateAndReduceGameState`, failing loudly if a fixture event
 * is rejected so a malformed fixture cannot silently drop steps.
 *
 * Imports only the engine — no React, stores, auth, router, or WebSocket.
 */
export interface AugmentedWarReplaySnapshot {
  readonly index: number;
  readonly event: AugmentedWarReplayFixtureEvent;
  /** The reconstructed engine action, or null for the `init` snapshot. */
  readonly action: AugmentedWarAction | null;
  readonly state: AugmentedWarState;
}

function requireState(
  state: AugmentedWarState | null,
  eventType: string,
): AugmentedWarState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

export function buildAugmentedWarReplaySnapshots(
  events: readonly AugmentedWarReplayFixtureEvent[],
): readonly AugmentedWarReplaySnapshot[] {
  const snapshots: AugmentedWarReplaySnapshot[] = [];
  let state: AugmentedWarState | null = null;

  for (const event of events) {
    if (event.eventType === "init") {
      state = (event.payload as unknown as AugmentedWarReplayInitPayload).state;
      snapshots.push({ index: snapshots.length, event, action: null, state });
      continue;
    }

    const current = requireState(state, event.eventType);
    const action = augmentedWarEventToAction(event.eventType, event.payload);
    const result = validateAndReduceGameState(current, action);
    if (!result.accepted) {
      throw new Error(
        `${event.eventType} (#${event.sequenceNumber}) rejected: ${
          result.error ?? "Validation failed"
        }`,
      );
    }
    state = result.state;
    snapshots.push({ index: snapshots.length, event, action, state });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty Augmented War event log");
  }

  return snapshots;
}
